import { enqueueSnackbar } from 'notistack';
import { FundsService } from 'api';
import { Logger } from 'utilities';
import { CashFlowSourceKind } from '../data/types';

export type VirtualExclusionItem = {
  sourceKind: CashFlowSourceKind;
  sourceId: number;
  isExcluded: boolean;
};

type ExclusionPayload = { is_excluded_from_calculations: boolean };
type ExclusionPatch = (sourceId: number, payload: ExclusionPayload) => Promise<unknown>;

// Unrealized-value rows have no fund source to toggle, so they have no entry here.
const EXCLUSION_PATCH_BY_KIND: Partial<Record<CashFlowSourceKind, ExclusionPatch>> = {
  shares_acquisition: (id, payload) => FundsService.apiFundsSharesAcquisitionExclusionPartialUpdate(id, payload),
  shares_sale: (id, payload) => FundsService.apiFundsSharesSaleExclusionPartialUpdate(id, payload),
  cash_distribution: (id, payload) => FundsService.apiFundsCashDistributionExclusionPartialUpdate(id, payload),
};

// Persists exclusion toggles for fund-derived rows in parallel, then refetches the ledger once and reports failures once.
export const persistVirtualExclusions = async (
  items: VirtualExclusionItem[],
  refetchLedger: () => Promise<void>
) => {
  const requests = items.flatMap(({ sourceKind, sourceId, isExcluded }) => {
    const patch = EXCLUSION_PATCH_BY_KIND[sourceKind];
    return patch ? [patch(sourceId, { is_excluded_from_calculations: isExcluded })] : [];
  });
  if (!requests.length) {
    return;
  }
  const failures = (await Promise.allSettled(requests)).filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  failures.forEach(({ reason }) => Logger.error('Failed to persist CFL exclusion toggle on virtual row', reason));
  try {
    await refetchLedger();
  } catch (error) {
    Logger.error('Failed to refresh the CFL after virtual exclusion toggles', error);
    enqueueSnackbar(
      'Could not refresh the ledger after updating exclusions. Reload the page to see current values.',
      { variant: 'error' }
    );
  }
  if (failures.length) {
    enqueueSnackbar(`${failures.length} of ${requests.length} rows could not be updated`, { variant: 'error' });
  }
};
